import { useState, useMemo } from 'react';
import { Button } from '@/components/ui/Button';
import { publicQuotesApi } from '@/services/public-quotes';
import { calculateMaxPerPlate, fitsOnPlate } from '@/utils/build-plate';

interface BoundingBox {
  readonly width: number;
  readonly height: number;
  readonly depth: number;
}

interface PublicCheckoutPanelProps {
  readonly token: string;
  readonly title: string;
  readonly volumeCm3: number;
  readonly materialId: string | null;
  readonly materialName: string | null;
  readonly color: string | null;
  readonly price: number | null;
  readonly rawPrice: number | null;
  readonly estimatedDays: number | null;
  readonly boundingBox: BoundingBox;
  /** Pre-filled email for authenticated users. When set, the email field is hidden. */
  readonly userEmail?: string | null;
}

const MIN_QUANTITY = 1;
const MINIMUM_ORDER_PRICE = 5.0;

export const PublicCheckoutPanel = ({
  token,
  title,
  volumeCm3,
  materialId,
  materialName,
  color,
  price,
  rawPrice,
  estimatedDays,
  boundingBox,
  userEmail,
}: PublicCheckoutPanelProps) => {
  const [quantity, setQuantity] = useState(1);
  const [emailInput, setEmailInput] = useState('');
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const email = userEmail || emailInput;
  const isLoggedIn = !!userEmail;

  const maxQuantity = useMemo(
    () => calculateMaxPerPlate(boundingBox),
    [boundingBox],
  );

  const modelFits = fitsOnPlate(boundingBox);
  const isEmailValid = isLoggedIn || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  const isComplete = !!materialId && !!color && !!price;
  const canCheckout = isComplete && termsAccepted && isEmailValid && modelFits;

  const effectiveRawPrice = rawPrice ?? price;
  const rawSubtotal = effectiveRawPrice
    ? Math.max(effectiveRawPrice * quantity, MINIMUM_ORDER_PRICE)
    : null;
  const subtotal = rawSubtotal
    ? Math.round(rawSubtotal * 100) / 100
    : null;
  const baselineActive = effectiveRawPrice !== null && effectiveRawPrice * quantity < MINIMUM_ORDER_PRICE;

  const getButtonLabel = (): string => {
    if (!modelFits) return 'Model exceeds build volume';
    if (isLoading) return 'Redirecting to payment...';
    if (!materialId) return 'Select a material';
    if (!color) return 'Select a color';
    if (!isLoggedIn && !isEmailValid && emailInput.length > 0) return 'Enter a valid email';
    if (!isLoggedIn && !emailInput) return 'Enter your email';
    if (!termsAccepted) return 'Accept terms to continue';
    return 'Proceed to Checkout';
  };

  const handleCheckout = async () => {
    if (!canCheckout || !materialId || !color) return;

    setIsLoading(true);
    setError(null);

    try {
      const sessionUrl = await publicQuotesApi.checkout(token, {
        materialId,
        color,
        quantity,
        customerEmail: email,
      });
      window.location.href = sessionUrl;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Checkout failed';
      setError(message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div
      style={{
        borderRadius: 'var(--radius-xl)',
        border: '1px solid var(--border-default)',
        backgroundColor: 'var(--bg-surface)',
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <div
        style={{
          padding: 'var(--space-3) var(--space-5)',
          borderBottom: '1px solid var(--border-subtle)',
          backgroundColor: 'var(--bg-surface-sunken)',
        }}
      >
        <h3
          style={{
            fontSize: 'var(--text-xs)',
            fontWeight: 'var(--font-weight-semibold)',
            textTransform: 'uppercase',
            letterSpacing: '0.08em',
            color: 'var(--text-secondary)',
          }}
        >
          Order Summary
        </h3>
      </div>

      <div
        style={{
          padding: 'var(--space-5)',
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--space-4)',
        }}
      >
        {/* File */}
        <SummaryRow label="File" value={title} />

        {/* Volume */}
        <SummaryRow label="Volume" value={`${volumeCm3} cm\u00B3`} />

        {/* Material */}
        <SummaryRow
          label="Material"
          value={materialName ?? undefined}
          placeholder="Not selected"
        />

        {/* Color */}
        <SummaryRow
          label="Color"
          value={color ? capitalize(color) : undefined}
          placeholder={materialId ? 'Not selected' : '\u2014'}
        />

        {/* Build volume warning */}
        {!modelFits && (
          <div
            style={{
              borderRadius: 'var(--radius-lg)',
              backgroundColor: 'var(--bg-error-subtle)',
              padding: 'var(--space-3)',
              fontSize: 'var(--text-xs)',
              color: 'var(--color-error-700)',
              lineHeight: 'var(--leading-relaxed)',
            }}
          >
            This model exceeds the maximum build volume (256 x 256 x 256 mm) and cannot
            be printed.
          </div>
        )}

        {/* Quantity */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
          <span
            style={{
              fontSize: 'var(--text-xs)',
              color: 'var(--text-tertiary)',
              textTransform: 'uppercase',
              letterSpacing: '0.06em',
            }}
          >
            Quantity
          </span>
          <div className="flex items-center" style={{ gap: 'var(--space-2)' }}>
            <button
              type="button"
              onClick={() => setQuantity((q) => Math.max(MIN_QUANTITY, q - 1))}
              disabled={quantity <= MIN_QUANTITY}
              style={{
                width: '2.5rem',
                height: '2.5rem',
                borderRadius: 'var(--radius-md)',
                border: '1px solid var(--border-default)',
                backgroundColor: 'var(--bg-surface)',
                color: 'var(--text-primary)',
                fontSize: 'var(--text-lg)',
                cursor: quantity <= MIN_QUANTITY ? 'not-allowed' : 'pointer',
                opacity: quantity <= MIN_QUANTITY ? 0.4 : 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              -
            </button>
            <input
              type="number"
              min={MIN_QUANTITY}
              max={maxQuantity}
              value={quantity}
              onChange={(e) => {
                const val = parseInt(e.target.value, 10);
                if (!isNaN(val)) {
                  setQuantity(Math.min(maxQuantity, Math.max(MIN_QUANTITY, val)));
                }
              }}
              style={{
                width: '3.5rem',
                textAlign: 'center',
                borderRadius: 'var(--radius-md)',
                border: '1px solid var(--border-default)',
                padding: 'var(--space-1) var(--space-2)',
                fontSize: 'var(--text-sm)',
                fontWeight: 'var(--font-weight-medium)',
                color: 'var(--text-primary)',
                backgroundColor: 'var(--bg-surface)',
              }}
            />
            <button
              type="button"
              onClick={() => setQuantity((q) => Math.min(maxQuantity, q + 1))}
              disabled={quantity >= maxQuantity}
              style={{
                width: '2.5rem',
                height: '2.5rem',
                borderRadius: 'var(--radius-md)',
                border: '1px solid var(--border-default)',
                backgroundColor: 'var(--bg-surface)',
                color: 'var(--text-primary)',
                fontSize: 'var(--text-lg)',
                cursor: quantity >= maxQuantity ? 'not-allowed' : 'pointer',
                opacity: quantity >= maxQuantity ? 0.4 : 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              +
            </button>
          </div>
          {maxQuantity < 100 && (
            <p
              style={{
                marginTop: 'var(--space-1)',
                fontSize: 'var(--text-xs)',
                color: 'var(--text-tertiary)',
                lineHeight: 'var(--leading-relaxed)',
              }}
            >
              Max {maxQuantity} per build plate. For larger quantities, place additional orders.
            </p>
          )}
        </div>

        {/* Email -- hidden for authenticated users */}
        {isLoggedIn ? (
          <SummaryRow label="Email" value={userEmail} />
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <label
              htmlFor="checkout-email"
              style={{
                fontSize: 'var(--text-xs)',
                color: 'var(--text-tertiary)',
                textTransform: 'uppercase',
                letterSpacing: '0.06em',
              }}
            >
              Email
            </label>
            <input
              id="checkout-email"
              type="email"
              value={emailInput}
              onChange={(e) => setEmailInput(e.target.value)}
              placeholder="your@email.com"
              style={{
                borderRadius: 'var(--radius-md)',
                border: '1px solid var(--border-default)',
                padding: 'var(--space-2) var(--space-3)',
                fontSize: 'var(--text-sm)',
                color: 'var(--text-primary)',
                backgroundColor: 'var(--bg-surface)',
                minHeight: '40px',
              }}
            />
            <p
              style={{
                fontSize: 'var(--text-xs)',
                color: 'var(--text-tertiary)',
              }}
            >
              Order confirmation will be sent to this address.
            </p>
          </div>
        )}

        {/* Divider */}
        <hr style={{ border: 'none', borderTop: '1px solid var(--border-default)', margin: 0 }} />

        {/* Pricing */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
          {quantity > 1 && effectiveRawPrice && (
            <PriceLine label="Unit cost" value={`$${effectiveRawPrice.toFixed(2)}`} muted />
          )}
          {baselineActive && (
            <PriceLine label="Order minimum" value={`$${MINIMUM_ORDER_PRICE.toFixed(2)}`} muted />
          )}
          <PriceLine
            label={quantity > 1 ? `Subtotal (x${quantity})` : 'Subtotal'}
            value={rawSubtotal ? `$${rawSubtotal.toFixed(2)}` : '\u2014'}
          />
          <PriceLine label="Shipping" value="At checkout" muted />
          <PriceLine label="Tax" value="At checkout" muted />
        </div>

        {estimatedDays && (
          <p
            style={{
              fontSize: 'var(--text-xs)',
              color: 'var(--text-tertiary)',
            }}
          >
            Est. production: {estimatedDays} business day{estimatedDays !== 1 ? 's' : ''}
          </p>
        )}

        {/* Total */}
        {subtotal && (
          <>
            <hr style={{ border: 'none', borderTop: '1px solid var(--border-default)', margin: 0 }} />
            <div className="flex justify-between items-center">
              <span
                style={{
                  fontWeight: 'var(--font-weight-semibold)',
                  color: 'var(--text-primary)',
                }}
              >
                Total
              </span>
              <span
                style={{
                  fontSize: 'var(--text-xl)',
                  fontWeight: 'var(--font-weight-bold)',
                  color: 'var(--color-primary-600)',
                }}
              >
                ${subtotal.toFixed(2)}
              </span>
            </div>
          </>
        )}

        {/* Legal checkboxes -- visible once selections are complete */}
        {isComplete && (
          <>
            <hr style={{ border: 'none', borderTop: '1px solid var(--border-default)', margin: 0 }} />
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
              <label
                className="flex items-start"
                style={{ gap: 'var(--space-2)', fontSize: 'var(--text-xs)', cursor: 'pointer' }}
              >
                <input
                  type="checkbox"
                  checked={termsAccepted}
                  onChange={(e) => setTermsAccepted(e.target.checked)}
                  style={{ marginTop: '2px', flexShrink: 0 }}
                />
                <span style={{ color: 'var(--text-secondary)', lineHeight: 'var(--leading-relaxed)' }}>
                  I accept the terms of service and refund policy. Printed items are
                  non-returnable unless defective.
                </span>
              </label>
            </div>
          </>
        )}

        {/* Checkout CTA */}
        <Button
          onClick={handleCheckout}
          disabled={!canCheckout || isLoading}
          size="lg"
          style={{ width: '100%', marginTop: 'var(--space-1)' }}
        >
          {getButtonLabel()}
        </Button>

        {error && (
          <p
            style={{
              fontSize: 'var(--text-xs)',
              color: 'var(--color-error-500)',
              textAlign: 'center',
            }}
          >
            {error}
          </p>
        )}

        {/* Trust line */}
        <div
          className="flex items-center justify-center"
          style={{
            gap: 'var(--space-2)',
            fontSize: 'var(--text-xs)',
            color: 'var(--text-tertiary)',
          }}
        >
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={2}
            style={{ height: '0.875rem', width: '0.875rem' }}
          >
            <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
            <path d="M7 11V7a5 5 0 0110 0v4" />
          </svg>
          <span>Secure checkout via Stripe</span>
        </div>
      </div>
    </div>
  );
};

/* ---- helpers ---- */

const SummaryRow = ({
  label,
  value,
  placeholder,
}: {
  readonly label: string;
  readonly value?: string;
  readonly placeholder?: string;
}) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
    <span
      style={{
        fontSize: 'var(--text-xs)',
        color: 'var(--text-tertiary)',
        textTransform: 'uppercase',
        letterSpacing: '0.06em',
      }}
    >
      {label}
    </span>
    {value ? (
      <span
        className="truncate"
        style={{
          fontSize: 'var(--text-sm)',
          fontWeight: 'var(--font-weight-medium)',
          color: 'var(--text-primary)',
        }}
      >
        {value}
      </span>
    ) : (
      <span
        style={{
          fontSize: 'var(--text-sm)',
          color: 'var(--text-tertiary)',
          fontStyle: 'italic',
        }}
      >
        {placeholder ?? '\u2014'}
      </span>
    )}
  </div>
);

const PriceLine = ({
  label,
  value,
  muted,
}: {
  readonly label: string;
  readonly value: string;
  readonly muted?: boolean;
}) => (
  <div className="flex justify-between" style={{ fontSize: 'var(--text-sm)' }}>
    <span style={{ color: 'var(--text-secondary)' }}>{label}</span>
    <span
      style={{
        fontWeight: muted ? undefined : 'var(--font-weight-medium)',
        color: muted ? 'var(--text-tertiary)' : 'var(--text-primary)',
        fontSize: muted ? 'var(--text-xs)' : undefined,
      }}
    >
      {value}
    </span>
  </div>
);

const capitalize = (s: string): string =>
  s.charAt(0).toUpperCase() + s.slice(1);
